import type { Session } from "@supabase/supabase-js";

type SessionResponse = {
  data?: { session?: Session | null } | null;
  error?: { message?: string } | null;
};

type AuthLike = {
  auth: {
    getSession: () => Promise<SessionResponse>;
  };
};

function sessionFromStoredValue(value: unknown): Session | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const candidate =
    (record.currentSession as Session | undefined) ??
    (record.session as Session | undefined) ??
    (record as unknown as Session);

  if (!candidate?.access_token || !candidate.user?.id) return null;
  const expiresAt = Number(candidate.expires_at ?? 0);
  if (expiresAt > 0 && expiresAt * 1000 < Date.now() - 60_000) return null;
  return candidate;
}

export function readStoredAuthSession(): Session | null {
  if (typeof window === "undefined") return null;
  const keys = Object.keys(window.localStorage).filter(
    (key) => key.startsWith("sb-") && key.endsWith("-auth-token"),
  );

  for (const key of keys) {
    try {
      const session = sessionFromStoredValue(
        JSON.parse(window.localStorage.getItem(key) ?? "null"),
      );
      if (session) return session;
    } catch {
      // Ignore malformed local storage values and keep looking.
    }
  }

  return null;
}

export function getStoredAccessToken(): string | null {
  return readStoredAuthSession()?.access_token ?? null;
}

export async function resolveAuthSession(
  authClient: AuthLike,
  timeoutMs = 1_500,
): Promise<Session | null> {
  const stored = readStoredAuthSession();
  let timer: number | undefined;

  try {
    const liveSession = authClient.auth
      .getSession()
      .then(({ data }) => data?.session ?? stored)
      .catch(() => stored);
    const timeout = new Promise<Session | null>((resolve) => {
      timer = window.setTimeout(() => resolve(stored), timeoutMs);
    });

    return await Promise.race([liveSession, timeout]);
  } finally {
    if (timer) window.clearTimeout(timer);
  }
}
/**
 * True when this page is a Lovable preview running inside the editor frame,
 * where the sign-in session is held by the editor and read over postMessage
 * instead of localStorage.
 *
 * That read is asynchronous and can time out, which briefly looks exactly
 * like "signed out". Nothing may conclude the viewer is a guest from a single
 * empty read in this context — an authenticated admin was shown the
 * members-only lock screen that way.
 */
export function isBrokeredPreviewAuth(): boolean {
  if (typeof window === "undefined") return false;
  try {
    const framed = !!window.parent && window.parent !== window;
    if (!framed) return false;
    const host = window.location.hostname;
    const zones = [
      "lovableproject.com",
      "lovableproject-dev.com",
      "lovable.app",
      "gpt-eng.com",
      "gptengineer.run",
    ];
    const onPreviewZone = zones.some((z) => host === z || host.endsWith("." + z));
    if (!onPreviewZone) return false;
    // A local token means storage is not brokered for this surface.
    return !Object.keys(window.localStorage).some(
      (key) => key.startsWith("sb-") && key.endsWith("-auth-token"),
    );
  } catch {
    return false;
  }
}
